import { Tabs } from '@ark-ui/react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { serviceTypeaheadValueSchema } from 'src/pages/api/services-search/typeahead';

import { useDictionary } from 'src/hooks/useDictionary';

import { addQueryToRecentSearches } from 'src/utils/recentSearchesFromLocalStorage';

import Placeholders from 'components/BaseHelpers/Placeholders';
import SearchCombobox from 'components/BaseHelpers/SearchCombobox';
import StartIndex from 'components/BaseHelpers/StartIndex';
import StopIndex from 'components/BaseHelpers/StopIndex';
import Text from 'components/BaseHelpers/Text';
import TwoColumnLayout from 'components/BaseHelpers/TwoColumnLayout';

import { ComponentProps } from '../../../types/component-props';
import { CoreServicesListingTabsData } from '../../../types/components';
import { getTypeaheadResultsFactory } from '../CoreGlobalNavigationHeader/components/SearchInput/getTypeaheadResult';
import AlphaServiceList from './components/AlphaServiceList';
import ServicesList from './components/ServicesList';

import styles from './CoreServicesListingTabs.module.scss';

export type CoreServicesListingTabsProps = ComponentProps & CoreServicesListingTabsData;

const TAB_QUERY_PARAM = 'tab';

const getUrlParts = (asPath: string): { hash: string; pathname: string; queryString: string } => {
  const [pathWithQuery, hash = ''] = asPath.split('#');
  const [pathname, queryString = ''] = pathWithQuery.split('?');

  return { hash, pathname, queryString };
};

const CoreServicesListingTabs = (props: CoreServicesListingTabsProps): JSX.Element => {
  const { tabs, placeholderText, typeaheadResultsCount, recentSearchesLabel } = props.fields;
  const tabsToRender = useMemo(() => tabs.filter((tab) => tab.services.length > 0), [tabs]);
  const defaultTab = tabsToRender[0]?.label ?? '';
  const tabLabels = useMemo(() => tabsToRender.map((tab) => tab.label), [tabsToRender]);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const { filtersLabel } = useDictionary();
  const localStorageKey = 'servicesLandingRecentSearches';
  const router = useRouter();

  const getValidTab = useCallback(
    (value: string | null): string | undefined => {
      if (!value) {
        return undefined;
      }

      return tabLabels.includes(value) ? value : undefined;
    },
    [tabLabels]
  );

  const replaceTabQueryParam = useCallback(
    (tab: string): void => {
      const { hash, pathname, queryString } = getUrlParts(router.asPath);
      const params = new URLSearchParams(queryString);
      params.set(TAB_QUERY_PARAM, tab);

      const nextQueryString = params.toString();
      const nextUrl = `${pathname}${nextQueryString ? `?${nextQueryString}` : ''}${
        hash ? `#${hash}` : ''
      }`;

      void router.replace(nextUrl, undefined, { shallow: true, scroll: false });
    },
    [router]
  );

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const { queryString } = getUrlParts(router.asPath);
    const params = new URLSearchParams(queryString);
    const queryTab = getValidTab(params.get(TAB_QUERY_PARAM));

    setActiveTab(queryTab ?? defaultTab);
  }, [defaultTab, getValidTab, router.asPath, router.isReady]);

  if (tabsToRender.length === 0) {
    return <></>;
  }

  const TabsList = (): JSX.Element => {
    return (
      <div className={styles['left-column']}>
        <SearchCombobox
          clearInputOnTrigger
          getTypeaheadResult={getTypeaheadResultsFactory('/api/services-search/typeahead')}
          hideClearButton
          localStorageKey={localStorageKey}
          onSubmit={() => {}}
          placeholder={placeholderText}
          recentSearchesLabel={recentSearchesLabel}
          typeaheadResultsCount={typeaheadResultsCount}
          onSelect={(e) => {
            const result = serviceTypeaheadValueSchema.safeParse(JSON.parse(e.itemValue));
            if (!result.success) {
              console.error('Invalid typeahead value:', result.error);
              return;
            }
            const { href, title } = result.data;
            addQueryToRecentSearches(
              {
                title: title,
                value: e.itemValue,
              },
              localStorageKey
            );
            router.push(href);
          }}
        />

        <div className={styles['tabs-list-wrapper']}>
          <span className={cn('body-eyebrow')}>{filtersLabel}</span>
          <Tabs.List className={styles.list}>
            {tabsToRender.map((tab) => (
              <Tabs.Trigger
                key={tab.label}
                className={cn(styles.trigger, 'body-sm')}
                value={tab.label}
              >
                <span className={styles.circle} />
                <Text field={tab.label} />
              </Tabs.Trigger>
            ))}
          </Tabs.List>
        </div>
      </div>
    );
  };

  const TabsContent = (): JSX.Element[] =>
    tabsToRender.map((tab) => (
      <Tabs.Content key={tab.label} value={tab.label}>
        {tab.type === 'alpha-list' ? (
          <AlphaServiceList list={tab.services} />
        ) : (
          <ServicesList list={tab.services} />
        )}
      </Tabs.Content>
    ));

  return (
    <>
      <StopIndex />
      <Tabs.Root
        value={activeTab}
        orientation="vertical"
        activationMode="manual"
        onValueChange={(details) => {
          const nextTab = getValidTab(details.value);

          if (!nextTab) {
            return;
          }

          setActiveTab(nextTab);
          replaceTabQueryParam(nextTab);
        }}
      >
        <div className={styles.main}>
          <TwoColumnLayout
            leftChildren={
              <>
                <TabsList />
              </>
            }
            rightChildren={
              <>
                <Placeholders placeholders={props.placeholders.rightContent} />
                <TabsContent />
              </>
            }
          />
        </div>
      </Tabs.Root>
      <StartIndex />
    </>
  );
};

export default CoreServicesListingTabs;
